// Batch 1 (UX/IA overhaul) — the shared per-NVR grouping/filter key.
//
// Mirrors groupCameras: prefer the (dormant) source_id, then nvr_serial (the
// real per-NVR key on the cameras row today), then a make/model label. Used
// by every tab that groups or filters cameras by NVR (Cameras, Live view,
// Health) so the key derivation is identical everywhere.
import type { CameraRef } from "@/types/deviceRef";

const NONE = "__none__";

// Only the NVR-identity subset is needed — not a full CameraRef — so callers
// can pass any camera-shaped object (e.g. the FE Camera, or a live-tile row).
export type NvrIdentity = Pick<
  CameraRef,
  "source_id" | "nvr_serial" | "nvr_make" | "nvr_model"
>;

export function nvrKey(cam: NvrIdentity): string {
  return (
    cam.source_id ||
    cam.nvr_serial ||
    [cam.nvr_make, cam.nvr_model].filter(Boolean).join(" ") ||
    NONE
  );
}

export function nvrKeyLabel(cam: NvrIdentity): string {
  // T3c — fall back to the SERIAL before giving up and saying "Ungrouped".
  // On this fleet make/model are NULL on every flat camera row (they live in
  // P1B and the projection has not run) while nvr_serial is present on all
  // 15, so the dropdown listed every distinct NVR under one indistinguishable
  // "Ungrouped" entry. Kept identical to groupCameras' labelling so the
  // Cameras tab, Live view and Health agree — the reason nvrKey exists.
  const makeModel = [cam.nvr_make, cam.nvr_model].filter(Boolean).join(" ");
  if (makeModel) return makeModel;
  const serial = (cam.nvr_serial ?? "").trim();
  if (serial) return `NVR ${serial.slice(-8)}`;
  return "Ungrouped";
}

/** Distinct {value,label} NVR options for a filter dropdown, sorted by label. */
export function nvrFilterOptions(
  cameras: NvrIdentity[],
): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const c of cameras) {
    const k = nvrKey(c);
    if (!seen.has(k)) seen.set(k, nvrKeyLabel(c));
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
